import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { HiOutlineRefresh, HiOutlineSave } from 'react-icons/hi';

interface Setting {
  key: string;
  value: any;
  category: string;
  description?: string;
}

export default function AdminContent() {
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const loadSettings = async () => {
    try {
      const response = await fetch('/api/settings');
      if (!response.ok) throw new Error('Failed to load settings');

      const data = await response.json();
      // Ensure data is an object
      setSettings(data && typeof data === 'object' ? data : {});
    } catch (error) {
      console.error('Load settings error:', error);
      toast({
        title: 'Error',
        description: 'Failed to load settings',
        variant: 'destructive',
      });
      // Set empty object on error
      setSettings({});
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaults = async () => {
    try {
      const response = await fetch('/api/settings/initialize', {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to initialize');

      toast({
        title: 'Success',
        description: 'Default settings initialized',
      });

      loadSettings();
    } catch (error) {
      console.error('Initialize error:', error);
      toast({
        title: 'Error',
        description: 'Failed to initialize settings',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const updateSetting = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const saveSetting = async (key: string, value: any, category: string) => {
    try {
      const response = await fetch(`/api/settings/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value, category }),
      });

      if (!response.ok) throw new Error('Failed to save');

      toast({
        title: 'Saved',
        description: `${key} updated successfully`,
      });
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: 'Error',
        description: 'Failed to save setting',
        variant: 'destructive',
      });
    }
  };

  const saveAllSettings = async () => {
    setSaving(true);
    try {
      const settingsArray = Object.entries(settings).map(([key, value]) => {
        const category = key.split('.')[0];
        return { key, value, category };
      });

      const response = await fetch('/api/settings/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ settings: settingsArray }),
      });

      if (!response.ok) throw new Error('Failed to save');

      toast({
        title: 'Success',
        description: 'All settings saved successfully',
      });
    } catch (error) {
      console.error('Save all error:', error);
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#0ea5e9]" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-bebas text-[2.6rem] leading-none tracking-[0.03em] text-[#0a2540]">
            Site Content Editor
          </h1>
          <p className="font-sans font-light text-sm text-[#94a3b8] mt-1">
            Edit all frontend content, text, and settings
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={initializeDefaults}
            variant="outline"
            className="gap-2"
          >
            <HiOutlineRefresh className="w-4 h-4" />
            Initialize Defaults
          </Button>
          <Button onClick={saveAllSettings} disabled={saving} className="gap-2">
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <HiOutlineSave className="w-4 h-4" />
            )}
            Save All Changes
          </Button>
        </div>
      </div>

      <Tabs defaultValue="hero" className="space-y-6">
        <TabsList>
          <TabsTrigger value="hero">Hero Section</TabsTrigger>
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="contact">Contact Info</TabsTrigger>
          <TabsTrigger value="general">General</TabsTrigger>
        </TabsList>

        {/* Hero Section */}
        <TabsContent value="hero" className="space-y-4">
          <Card className="p-6">
            <h2 className="font-bebas text-2xl text-[#0a2540] mb-4">
              Hero Section
            </h2>

            <div className="space-y-4">
              <div>
                <Label htmlFor="hero-title">Main Title</Label>
                <Input
                  id="hero-title"
                  value={settings['hero.title'] || ''}
                  onChange={e => updateSetting('hero.title', e.target.value)}
                  placeholder="Master High-Rise Window Cleaning"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="hero-subtitle">Subtitle</Label>
                <Input
                  id="hero-subtitle"
                  value={settings['hero.subtitle'] || ''}
                  onChange={e => updateSetting('hero.subtitle', e.target.value)}
                  placeholder="Professional training for commercial window cleaning specialists"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="hero-cta">CTA Button Text</Label>
                <Input
                  id="hero-cta"
                  value={settings['hero.cta_text'] || ''}
                  onChange={e => updateSetting('hero.cta_text', e.target.value)}
                  placeholder="Enroll Now"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="hero-video">Background Video URL</Label>
                <Input
                  id="hero-video"
                  value={settings['hero.video_url'] || ''}
                  onChange={e =>
                    updateSetting('hero.video_url', e.target.value)
                  }
                  placeholder="https://example.com/video.mp4"
                  className="mt-2"
                />
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Services Section */}
        <TabsContent value="services" className="space-y-4">
          <Card className="p-6">
            <h2 className="font-bebas text-2xl text-[#0a2540] mb-4">
              Services Section
            </h2>

            <div className="space-y-4">
              <div>
                <Label htmlFor="services-title">Section Title</Label>
                <Input
                  id="services-title"
                  value={settings['services.title'] || ''}
                  onChange={e =>
                    updateSetting('services.title', e.target.value)
                  }
                  placeholder="Our Training Programs"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="services-subtitle">Section Subtitle</Label>
                <Textarea
                  id="services-subtitle"
                  value={settings['services.subtitle'] || ''}
                  onChange={e =>
                    updateSetting('services.subtitle', e.target.value)
                  }
                  placeholder="Comprehensive courses designed for all skill levels"
                  className="mt-2"
                  rows={3}
                />
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Contact Info */}
        <TabsContent value="contact" className="space-y-4">
          <Card className="p-6">
            <h2 className="font-bebas text-2xl text-[#0a2540] mb-4">
              Contact Information
            </h2>

            <div className="space-y-4">
              <div>
                <Label htmlFor="contact-phone">Phone Number</Label>
                <Input
                  id="contact-phone"
                  value={settings['contact.phone'] || ''}
                  onChange={e => updateSetting('contact.phone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="contact-email">Email Address</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={settings['contact.email'] || ''}
                  onChange={e => updateSetting('contact.email', e.target.value)}
                  placeholder="info@windowcleaningschool.com"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="contact-address">Physical Address</Label>
                <Textarea
                  id="contact-address"
                  value={settings['contact.address'] || ''}
                  onChange={e =>
                    updateSetting('contact.address', e.target.value)
                  }
                  placeholder="123 Training Center Dr, Miami, FL 33101"
                  className="mt-2"
                  rows={3}
                />
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-4">
          <Card className="p-6">
            <h2 className="font-bebas text-2xl text-[#0a2540] mb-4">
              General Settings
            </h2>

            <div className="space-y-4">
              <div>
                <Label htmlFor="general-sitename">Site Name</Label>
                <Input
                  id="general-sitename"
                  value={settings['general.site_name'] || ''}
                  onChange={e =>
                    updateSetting('general.site_name', e.target.value)
                  }
                  placeholder="Window Cleaning School"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="general-tagline">Tagline</Label>
                <Input
                  id="general-tagline"
                  value={settings['general.tagline'] || ''}
                  onChange={e =>
                    updateSetting('general.tagline', e.target.value)
                  }
                  placeholder="Professional High-Rise Window Cleaning Training"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="general-footer">Footer Text</Label>
                <Input
                  id="general-footer"
                  value={settings['general.footer_text'] || ''}
                  onChange={e =>
                    updateSetting('general.footer_text', e.target.value)
                  }
                  placeholder="© 2026 Window Cleaning School. All rights reserved."
                  className="mt-2"
                />
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-900">
          <strong>Note:</strong> Changes will be reflected on the frontend
          immediately after saving. Make sure to review your changes on the live
          site.
        </p>
      </div>
    </div>
  );
}
